import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import { Badge } from "../../../components/ui/badge";
import { Save, Globe, AlertTriangle, ShieldCheck } from "lucide-react";
import { MKT, getAdminAuth } from "./AdminEmailMarketing";

export default function MarketingSettingsTab() {
  const [s, setS] = useState(null);
  const [domain, setDomain] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${MKT}/settings`, getAdminAuth()).then(r => setS(r.data)).catch(() => toast.error("Failed to load settings"));
    axios.get(`${MKT}/domain-status`, getAdminAuth()).then(r => setDomain(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${MKT}/settings`, s, getAdminAuth());
      setS(res.data);
      toast.success("Marketing settings saved");
    } catch (e) { toast.error("Failed to save"); }
    setSaving(false);
  };

  if (!s) return <p className="text-sm text-muted-foreground mt-4">Loading…</p>;
  const set = (k) => (e) => setS(p => ({ ...p, [k]: e.target?.value ?? e }));

  const DomainBadge = ({ ok, label }) => (
    <Badge className={`text-[10px] ${ok === true ? "bg-emerald-500/15 text-emerald-500" : ok === false ? "bg-red-500/15 text-red-400" : "bg-slate-500/15 text-slate-400"}`}>
      {label}: {ok === true ? "verified" : ok === false ? "not verified" : "unknown"}
    </Badge>
  );

  return (
    <div className="space-y-4 mt-4 max-w-3xl" data-testid="marketing-settings-tab">
      {/* Live sending master switch */}
      <Card className={`border ${s.live_sending_enabled ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}>
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" /> Live Marketing Sending
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {s.live_sending_enabled
                ? "ENABLED — campaigns will send real emails."
                : "DISABLED (safe mode) — campaigns cannot dispatch any real emails until you enable this."}
            </p>
          </div>
          <Switch checked={!!s.live_sending_enabled} onCheckedChange={(v) => setS(p => ({ ...p, live_sending_enabled: v }))} data-testid="live-sending-toggle" />
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-5 space-y-4">
          <p className="text-sm font-semibold text-foreground">Sender</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Marketing From Name</Label><Input value={s.from_name || ""} onChange={set("from_name")} data-testid="mkt-from-name" /></div>
            <div><Label>Marketing From Email</Label><Input value={s.from_email || ""} onChange={set("from_email")} placeholder="hello@updates.fieldzai.com" data-testid="mkt-from-email" /></div>
            <div><Label>Reply-To</Label><Input value={s.reply_to || ""} onChange={set("reply_to")} /></div>
            <div><Label>Company Name</Label><Input value={s.company_name || ""} onChange={set("company_name")} /></div>
            <div className="col-span-2">
              <Label>Physical Mailing Address (required for compliance)</Label>
              <Input value={s.postal_address || ""} onChange={set("postal_address")} placeholder="123 Main St, Suite 100, City, ST 00000, USA" data-testid="mkt-postal-address" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-5 space-y-3">
          <p className="text-sm font-semibold text-foreground flex items-center gap-2"><Globe className="w-4 h-4" /> Sending Domain</p>
          {domain?.found ? (
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-blue-500/15 text-blue-400 text-[10px]">{domain.domain}</Badge>
              <DomainBadge ok={domain.verified} label="Domain" />
              <DomainBadge ok={domain.spf} label="SPF" />
              <DomainBadge ok={domain.dkim} label="DKIM" />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              {domain?.error ? `Status check: ${domain.error}` :
                domain?.configured === false ? "MailerSend API key not configured." :
                `Domain "${domain?.domain || "—"}" not found in MailerSend. Available: ${(domain?.all_domains || []).join(", ") || "none"}`}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground">
            Recommended: use a dedicated subdomain (e.g. <b>updates.fieldzai.com</b>) for marketing so transactional email reputation stays protected. Add the subdomain in MailerSend, publish its SPF/DKIM/Return-Path DNS records, then set the From Email above to that subdomain.
          </p>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-5 space-y-4">
          <p className="text-sm font-semibold text-foreground">Sending & Tracking</p>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Batch size (per minute)</Label><Input type="number" value={s.batch_size} onChange={(e) => setS(p => ({ ...p, batch_size: parseInt(e.target.value || 0) }))} data-testid="mkt-batch-size" /></div>
            <div><Label>Max per hour</Label><Input type="number" value={s.max_per_hour} onChange={(e) => setS(p => ({ ...p, max_per_hour: parseInt(e.target.value || 0) }))} /></div>
            <div className="flex items-end gap-2 pb-1">
              <Switch checked={!!s.tracking_enabled} onCheckedChange={(v) => setS(p => ({ ...p, tracking_enabled: v }))} data-testid="mkt-tracking-toggle" />
              <Label>Open/click tracking</Label>
            </div>
          </div>
          <div>
            <Label>Footer text</Label>
            <Textarea rows={2} value={s.footer_text || ""} onChange={set("footer_text")} data-testid="mkt-footer-text" />
            <p className="text-[10px] text-muted-foreground mt-1">Shown above the unsubscribe links and postal address in every marketing email.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-5 space-y-4">
          <p className="text-sm font-semibold text-foreground flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-500" /> Deliverability Safety (Email Verification)
          </p>
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label>Require verified emails for campaigns</Label>
              <p className="text-[10px] text-muted-foreground mt-0.5">Only contacts verified as Valid can receive campaigns. Unverified, Invalid and Failed addresses are always blocked.</p>
            </div>
            <Switch checked={!!s.require_verification} onCheckedChange={(v) => setS(p => ({ ...p, require_verification: v }))} data-testid="require-verification-toggle" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex items-center gap-2">
              <Switch checked={!!s.send_catch_all} onCheckedChange={(v) => setS(p => ({ ...p, send_catch_all: v }))} data-testid="send-catch-all-toggle" />
              <Label className="text-xs">Allow Catch-All</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!s.send_risky} onCheckedChange={(v) => setS(p => ({ ...p, send_risky: v }))} data-testid="send-risky-toggle" />
              <Label className="text-xs">Allow Risky</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!s.send_unknown} onCheckedChange={(v) => setS(p => ({ ...p, send_unknown: v }))} data-testid="send-unknown-toggle" />
              <Label className="text-xs">Allow Unknown</Label>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground -mt-2">Recommended: keep all three OFF. Catch-all / risky / unknown results bounce far more often than Valid.</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Re-verify after (days)</Label>
              <Input type="number" value={s.verification_expiry_days ?? 90}
                onChange={(e) => setS(p => ({ ...p, verification_expiry_days: parseInt(e.target.value || 0) }))} data-testid="verification-expiry-input" />
            </div>
            <div>
              <Label>Auto-pause bounce threshold (%)</Label>
              <Input type="number" step="0.1" value={s.bounce_pause_threshold ?? 1.5}
                onChange={(e) => setS(p => ({ ...p, bounce_pause_threshold: parseFloat(e.target.value || 0) }))} data-testid="bounce-threshold-input" />
            </div>
            <div>
              <Label>Min sends before auto-pause</Label>
              <Input type="number" value={s.auto_pause_min_sends ?? 50}
                onChange={(e) => setS(p => ({ ...p, auto_pause_min_sends: parseInt(e.target.value || 0) }))} data-testid="auto-pause-min-sends-input" />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">
            A sending campaign automatically pauses when its hard-bounce rate exceeds the threshold. Transactional emails (invoices, password resets, notifications) are <b>never</b> affected by any of these rules.
          </p>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} data-testid="save-mkt-settings-btn"><Save className="w-4 h-4 mr-1" />Save Settings</Button>
    </div>
  );
}
